import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@paypalcorp/pp-react';
import { StyleSheet, css } from '../utility/paypalAphrodite';
import { copyToClipboard } from '../buttons/lib/copyToClipBoard';

const styles = StyleSheet.create({
  copyLinkFieldContainer: {
    borderBottom: '1px dashed #CBD2D6',
    padding: '16px 16px 24px',
    fontSize: 14,
    '@media @tabletAndMobile': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    ':last-child': {
      borderBottom: 'none',
    },
  },
  copyLinkField: {
    alignItems: 'center',
    backgroundColor: '#F5FBFE',
    height: 60,
    border: '1px dotted #979797',
    borderStyle: 'dotted',
    padding: '0px 10px',
    margin: '5px 0px',
    display: 'flex',
  },
  copyLinkTextarea: {
    height: 'auto',
    margin: 'auto',
    backgroundColor: '#F5FBFE',
    border: 'none',
    resize: 'none',
    overflow: 'hidden',
    width: '100%',
    outline: 'none',
    ':hover': {
      overflow: 'auto',
    },
  },
  copyLinkFieldCopied: {
    backgroundColor: '#F5FDFB',
  },
  copyLinkFieldIconContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    zIndex: 5,
    alignSelf: 'stretch',
    marginLeft: 6,
  },
  copyLinkFieldIcons: {
    marginTop: 12,
    marginBottom: 12,
    lineHeight: '1rem',
    cursor: 'pointer',
    minWidth: '4rem',
    '&.copied': {
      padding: 0,
    },
  },
  '.ppvx_icon-check-circle-alt': {
    fontSize: 12,
  },
  notCopied: {
    color: '#0070BA',
    background: 'none',
  },
  copied: {
    borderColor: '#018065',
    backgroundColor: '#018065',
    color: 'white',
  },
  copyLinkDescription: {
    padding: '8px 16px 20px',
  },
});

function CopyLink({ url, analytics, messages }) {
  const [isCopied, setIsCopied] = useState(false);
  const [padding, setPadding] = useState('0px');
  const copyRef = useRef(null);

  useEffect(() => {
    if (copyRef.current) {
      // hacky padding to center text in a textarea
      const current = copyRef.current;
      const innerHTML = current.innerHTML;
      if (!innerHTML || innerHTML.length > 48 || current.clientWidth < 195) {
        setPadding('0');
        return;
      }
      setPadding('2.5% 0px 2.5% 0px');
    }
  }, []);

  function handleCopyToClipBoard() {
    copyToClipboard('copy-link-text');
    if (analytics) {
      analytics('copy_link');
    }
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }

  return (
    typeof document !== 'undefined' &&
    document.queryCommandSupported('copy') && (
      <div
        className={`${css(styles.copyLinkFieldContainer)}`}
        id="copy-link-field-container"
      >
        <div
          className={`${css(styles.copyLinkField)} ${
            isCopied ? css(styles.copyLinkFieldCopied) : ''
          }`}
          onClick={handleCopyToClipBoard}
          data-testid="copy-link-field-click"
        >
          <textarea
            id="copy-link-text"
            className={`${css(styles.copyLinkTextarea)} ${
              isCopied ? css(styles.copyLinkFieldCopied) : ''
            }`}
            style={{ padding: padding }}
            ref={copyRef}
            value={url}
            readOnly
          >
            {url}
          </textarea>
          <div className={`${css(styles.copyLinkFieldIconContainer)}`}>
            {isCopied ? (
              <Button
                className={`${css(styles.copied)} ${css(
                  styles.copyLinkFieldIcons
                )}`}
                data-testid="copy-link-copied"
              >
                <Icon name="check-circle-alt" size="xs" />
              </Button>
            ) : (
              <Button
                className={`${css(styles.notCopied)} ${css(
                  styles.copyLinkFieldIcons
                )}`}
                data-testid="copy-link-not-copied"
              >
                {messages.copy}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  );
}

CopyLink.propTypes = {
  url: PropTypes.string,
  analytics: PropTypes.func,
  messages: PropTypes.object,
};

CopyLink.defaultProps = {
  analytics: null,
};

export default CopyLink;
